import { Component, OnDestroy, OnInit } from '@angular/core';
import { PermissionService } from "../../service/permission.service";
import { UserGroupManagement } from "../../model/userGroupManagement";
import { LoginIdService } from "../../service/login-id.service";
import { Subscription } from "rxjs";
import { Location } from "@angular/common";
import { AddUserDialogComponent } from "../dialogs/add-user-dialog/add-user-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { UserGroup } from "../../model/userGroup";
import { User } from "../../model/user";
import { UserServiceService } from "../../service/user-service.service";
import { UserGroupInvite } from "../../model/userGroupInvite";
import { EditUserGroupDialogComponent } from "../dialogs/edit-user-group-dialog/edit-user-group-dialog.component";
import { UserGroupDto } from "../../model/userGroupDto";
import { SnackbarService } from "../../service/snackbar.service";
import { startWith } from "rxjs/operators";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: 'app-user-group-management',
  templateUrl: './user-group-management.component.html',
  styleUrls: ['./user-group-management.component.css']
})
export class UserGroupManagementComponent implements OnInit, OnDestroy {
  protected readonly faArrowLeft = faArrowLeft;

  userGroupManagements: UserGroupManagement[] = [];
  orphans: User[] = [];
  openUserGroupInvites: UserGroupInvite[] = [];
  userGroupSubscription: Subscription;
  companyLoginId: string;
  isAllowedToEdit: boolean = false;
  isLoading: boolean = true;

  constructor(private permissionService: PermissionService,
              private loginIdService: LoginIdService,
              private userService: UserServiceService,
              public dialog: MatDialog,
              private location: Location,
              private snackbarService: SnackbarService) {
  }

  async ngOnInit(): Promise<any> {
    // wait until the loginIdService has been initialized
    await this.loginIdService.loginServiceInitialized$.toPromise();
    // initialize the view with the currently selected CompanyLogin
    this.userGroupSubscription = this.loginIdService.loginIdChanged$.pipe(startWith(this.loginIdService.getCurrentLoginId())).subscribe( loginId => {
      this.companyLoginId = loginId;
      this.initializeData(loginId);
    });
  }

  initializeData(loginId: string) {
    // only load data if the user is an admin within the CompanyLogin
    if(loginId && this.loginIdService.isAdmin()) {
      this.isLoading = true;
      // get the UserGroupManagement-List
      this.permissionService.getUserGroupManagement(loginId).subscribe(res => {
        this.userGroupManagements = [];
        res.forEach(management => {
          this.userGroupManagements.push(new UserGroupManagement(management));
        });
        this.companyLoginId = loginId;
        this.isAllowedToEdit = this.loginIdService.isAdmin();
        this.isLoading = false;
      }, error => {
        this.userGroupManagements = [];
        this.isAllowedToEdit = false;
        this.isLoading = false;
      });

      // get the orphaned users (Users without an assigned UserGroup)
      this.permissionService.getOrphanedUsersFromCompanyLogin(loginId).subscribe(res => {
        this.orphans = res;
      }, error => {
        this.orphans = [];
      });

      // get all open (PENDING, EXPIRED, INVALID) Invites from the whole CompanyLogin
      this.permissionService.getAllOpenUserGroupInvitesFromCompanyLogin(loginId).subscribe(res => {
        this.openUserGroupInvites = res;
        this.openUserGroupInvites.forEach(invite => {
          if(new Date(invite.expires) < new Date() && invite.status !== 'COMPLETE') {
            invite.status = 'EXPIRED';
          }
        })
      }, error => {
        this.openUserGroupInvites = [];
      });
    } else {
      // disable the component
      this.isAllowedToEdit = false;
      this.isLoading = false;
      this.orphans = [];
      this.userGroupManagements = [];
      this.openUserGroupInvites = [];
    }
  }

  navigateBack(): void {
    this.location.back();
  }

  addUserDialog():void {
      const dialogRef = this.dialog.open(AddUserDialogComponent, {
        width: 'auto',
        data: { user: null, groups: this.userGroupManagements }
      });

      dialogRef.afterClosed().subscribe(result => {
        if(result) { // create Invite or add new User to UserGroup
          // find selected UserGroupIndex by name
          const index = this.userGroupManagements.findIndex(group => group.userGroup.name === result.userGroupName);
          if(index !== -1) {
            const selectedGroup = this.userGroupManagements[index].userGroup;
            const mails = [result.userMail];
            this.setUpUserPermissions(selectedGroup, mails);
          }
        }
      })
  }

  moveUser(userMail: string, newGroupId: number): void {
    let foundUser = null;
    const oldGroupIndex = this.userGroupManagements.findIndex(group => group.users.find(user => user.mail === userMail));
    const newGroupIndex = this.userGroupManagements.findIndex(group => group.userGroup.id === newGroupId);
    const orphanIndex = this.orphans.findIndex(user => user.mail === userMail);

    // check within the UserGroupManagements and remove it there
    if(oldGroupIndex !== -1) { // was the user found within a UserGroup ? get the user and remove it from that group
      foundUser = this.userGroupManagements[oldGroupIndex].users.find(user => user.mail === userMail);
      this.userGroupManagements[oldGroupIndex].users = this.userGroupManagements[oldGroupIndex].users.filter(user => user.mail !== userMail);
    }

    // check within the Orphans and remove it there
    if(orphanIndex !== -1) {
      foundUser = this.orphans[orphanIndex];
      this.orphans.splice(orphanIndex, 1);
    }

    if(foundUser) { // did we find a user ? push it into the new UserGroup
      this.userGroupManagements[newGroupIndex].users.push(foundUser);
    } else { // it was an existing user that has to be fetched by the entered mail and pushed into the new group
      const mailUser = new User();
      mailUser.mail = userMail;
      this.userService.getUserByMail(mailUser).subscribe(res => {
        this.userGroupManagements[newGroupIndex].users.push(res);
      },
      error => {
        this.snackbarService.showSnackbar("Oops, da hat etwas nicht funktioniert!", false);
        console.error("Could not fetch user by mail :(")
      });
    }
  }

  disableUser(user: User) {
    this.orphans.push(user);
  }

  setUpUserPermissions(group: UserGroup, mails: string[]): void {
    const data = {
      loginId: group.companyLoginId,
      groupId: group.id,
      userMails: mails
    }

    this.permissionService.createInvitesAndAssignPermissions(data).subscribe(res => {
        const usersToMove = mails.filter(mail => !res.some(invite => invite.mail === mail));
        usersToMove.forEach(mail => {
            this.moveUser(mail, data.groupId);
        });
        this.openUserGroupInvites.push(...res);
    }, error => {
      console.log(error);
    });
  }

  filterUserGroup(array: UserGroupManagement[]): UserGroup[] {
    return array?.map(group => group.userGroup) || [];
  }

  filterInvitesForUserGroup(invites: UserGroupInvite[], userGroupId): UserGroupInvite[] {
    return userGroupId ? invites.filter(invite => invite.userGroupId === userGroupId) : [];
  }

  createUserGroupManagementFromUserGroup(userGroup: UserGroup, users: User[]): UserGroupManagement {
      const userGroupManagement = new UserGroupManagement(null);
      userGroupManagement.userGroup = userGroup;
      userGroupManagement.users = users ? users : [];
      return userGroupManagement;
  }

  // is called from the HTML-Template with custom parameters to only show the creation tab of the modal
  createUserGroup(event: any) {
    this.editUserGroup(event, null, 0, true, false);
  }

  // is called from the HTML-Template with custom parameters to only show the edit tab of the modal
  editUserGroup(event: any, userGroupId: number, index: number, hideEdit: boolean, hideCreate: boolean): void {
    event.stopPropagation(); // prevent the click to also trigger the accordion expansion
    const invites = this.filterInvitesForUserGroup(this.openUserGroupInvites, userGroupId);
    const dialogRef = this.dialog.open(EditUserGroupDialogComponent, {
      width: '900px',
      data: {groups: this.filterUserGroup(this.userGroupManagements), active: index, invites:invites, hideEdit:hideEdit, hideCreate:hideCreate},
      panelClass: 'altered-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        if(result.action === "create") {
          const userGroupDto = new UserGroupDto(result.data);
          this.permissionService.createUserGroup(userGroupDto).subscribe( response => {
            let group = new UserGroup(response)
            const userGroupManagement = this.createUserGroupManagementFromUserGroup(group, null);
            this.userGroupManagements.push(userGroupManagement);
            this.setUpUserPermissions(group, result.invites.mails);
            this.snackbarService.showSnackbar("Nutzergruppe erfolgreich erstellt", true);
          }, error => {
            this.snackbarService.showSnackbar("Fehler beim Erstellen der Nutzergruppe!", false);
          });
        }

        if(result.action === "edit") {
          const userGroupDto = new UserGroupDto(result.data);
          this.permissionService.updateUserGroup(userGroupDto).subscribe( response => {
            const index = this.userGroupManagements.findIndex( elem => elem.userGroup.id === response.id);
            if(index !== -1) {
              this.userGroupManagements = this.userGroupManagements.map(grp => {
                if(grp.userGroup.id === result.data.userGroupId) {
                  return this.createUserGroupManagementFromUserGroup(result.data, grp.users);
                }
                return grp;
              });
              this.snackbarService.showSnackbar("Nutzergruppe erfolgreich aktualisiert", true);
            } else {
              this.snackbarService.showSnackbar("Nutzergruppe konnte nicht aktualisiert werden", false);
            }
          });
        }

        if(result.action === "delete") {
          const userGroupDto = new UserGroupDto(result.data);
          this.permissionService.deleteUserGroup(userGroupDto.companyLoginId, userGroupDto.id).subscribe(response => {
            this.snackbarService.showSnackbar("Nutzergruppe erfolgreich gelöscht", true);

            this.permissionService.getOrphanedUsersFromCompanyLogin(this.loginIdService.getCurrentLoginId()).subscribe(res => {
                this.orphans = res;
            });

            const index = this.userGroupManagements.findIndex(group => group.userGroup.id === userGroupDto.id);
            this.userGroupManagements.splice(index, 1);
          }, error => {
            this.snackbarService.showSnackbar("Nutzergruppe konnte nicht gelöscht werden!", false);
          })
        }
      } // do nothing, just close
    });
  }

  // clean up
  ngOnDestroy(): void {
    this.userGroupSubscription.unsubscribe();
  }
}
